import { useEffect, useRef, useState } from "react"
import {
  CheckCircle,
  AlertCircle,
  User,
  MapPin,
  Building2,
  FileText,
  ClipboardCheck,
  HelpCircle,
  Play,
  BookOpen,
  Shield,
  Users,
  LifeBuoy,
  Briefcase,
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useEmployeeOnboarding } from "../../../employee/employee/hooks/employeeHook"
import WelcomeDialog from "../components/WelcomeDialog"
import type { EmployeeOnboardingDetailsDto } from "@/modules/employee/employee/dto/employeeDetails"
import {
  employeeProfileSchema,
  addressSchema,
  bankDetailsSchema,
} from "@/modules/employee/employee/schemas/employeeFormSchemas"
import { Onboardingstatus } from "@/modules/onboarding/onboarding-status"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/modules/shared/ui/components/dialog"
import { apiClient } from "@/lib/api-client"
import {
  ONBOARDING_WELCOME_CONTENT,
  type WelcomeContent,
} from "@/modules/employee/employee/dto/welcome"

const FORM_STEPS = [
  {
    id: 0,
    title: "Personal Information",
    description: "Your name, contact details, and identity",
    Icon: User,
  },
  {
    id: 1,
    title: "Address Details",
    description: "Your current residential address",
    Icon: MapPin,
  },
  {
    id: 2,
    title: "Banking Information",
    description: "Bank account details for payroll",
    Icon: Building2,
  },
  {
    id: 3,
    title: "Documents & Signatures",
    description: "Upload required documents and sign agreements",
    Icon: FileText,
  },
  {
    id: 4,
    title: "Review & Submit",
    description: "Review all your information and submit",
    Icon: ClipboardCheck,
  },
]

function hasUnresolvedFlag(
  data: EmployeeOnboardingDetailsDto,
  fieldName: string,
): boolean {
  return (data.fieldFlags || []).some(
    (f) => f.fieldName === fieldName && !f.resolvedAt,
  )
}

function getStepCompletion(
  data: EmployeeOnboardingDetailsDto | null | undefined,
): boolean[] {
  if (!data) return Array(5).fill(false)

  const isComplete =
    data.onboardingStatus === Onboardingstatus.COMPLETED ||
    data.onboardingStatus === Onboardingstatus.APPROVED ||
    data.onboardingStatus === Onboardingstatus.SUBMITTED

  // Validate each step against the same schemas the onboarding form uses, so
  // the dashboard's "complete" indicator can't disagree with what the form
  // considers valid (and gates Submit on). A step is complete only when it is
  // both schema-valid and free of unresolved review flags.
  const profileValid = employeeProfileSchema.safeParse({
    firstName: data.firstName,
    secondName: data.secondName,
    lastName: data.lastName,
    idType: data.identityType,
    idNumber: data.identityNumber,
    gender: data.gender,
    dateOfBirth: data.dateOfBirth,
    phone: data.phone,
    email: data.email,
    position: data.position,
    emergencyContactName: data.emergencyContactName,
    emergencyContactPhone: data.emergencyContactPhone,
    taxNumber: data.taxNumber,
    raceGroup: data.raceGroup,
    maritalStatus: data.maritalStatus,
  }).success
  const step0 = !!(
    profileValid &&
    !hasUnresolvedFlag(data, "personal") &&
    !hasUnresolvedFlag(data, "emergency")
  )

  const address = typeof data.address === "string" ? {} : data.address
  const step1 = !!(
    addressSchema.safeParse(address).success &&
    !hasUnresolvedFlag(data, "address")
  )

  const bankDetails =
    typeof data.bankDetails === "string" ? {} : data.bankDetails
  const step2 = !!(
    bankDetailsSchema.safeParse(bankDetails).success &&
    !hasUnresolvedFlag(data, "bank")
  )
  const requiredDocsOk =
    !data.requiredDocuments?.length ||
    data.requiredDocumentUploads?.every((d) => d.submitted) === true
  const signaturesOk =
    !data.documentsToBeSigned?.length ||
    data.documentsToBeSigned?.every((d) => d.hasSigned) === true
  const step3 = !!(
    requiredDocsOk &&
    signaturesOk &&
    !hasUnresolvedFlag(data, "documents")
  )
  const step4 = isComplete

  return [step0, step1, step2, step3, step4]
}

function getStepFlags(
  data: EmployeeOnboardingDetailsDto | null | undefined,
): boolean[] {
  if (!data) return Array(5).fill(false)
  return [
    hasUnresolvedFlag(data, "personal") || hasUnresolvedFlag(data, "emergency"),
    hasUnresolvedFlag(data, "address"),
    hasUnresolvedFlag(data, "bank"),
    hasUnresolvedFlag(data, "documents"),
    false,
  ]
}

function ProgressRing({ percent }: { percent: number }) {
  const r = 15.9155
  const circumference = 2 * Math.PI * r
  const dash = (percent / 100) * circumference
  return (
    <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
      <circle
        cx="18"
        cy="18"
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        className="text-border"
      />
      <circle
        cx="18"
        cy="18"
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circumference}`}
        className="text-primary transition-all duration-500"
      />
    </svg>
  )
}

const QUICK_RESOURCES = [
  {
    Icon: BookOpen,
    title: "Employee Handbook",
    description: "Culture, policies, and guidelines.",
    dark: false,
    content: `Welcome to Inspired! Our handbook covers everything you need to thrive here.

• Working Hours: Monday to Friday, 08:00 – 17:00, with a flexible one-hour lunch break.
• Leave Policy: 21 days of annual leave, 10 sick days, and 3 family responsibility days per year.
• Dress Code: Smart casual in the office; dress up for client meetings.
• Code of Conduct: Respect, integrity, and collaboration are non-negotiable.
• Communication: Slack for day-to-day, email for formal matters, Teams for meetings.
• Performance Reviews: Quarterly check-ins with your manager and an annual review in December.

For the full handbook PDF, contact the People team.`,
  },
  {
    Icon: Shield,
    title: "Benefits Guide",
    description: "Your perks and benefits overview.",
    dark: false,
    content: `Here's a snapshot of the perks that come with being part of the team:

• Medical Aid: Discovery Health – company contributes 50% of the premium.
• Retirement: 10% employer contribution to the group provident fund.
• Life & Disability Cover: 3x annual salary group life cover, included at no cost.
• Wellness: Free access to the Discovery Vitality programme and monthly wellness stipend.
• Learning Budget: R10,000 per year for courses, conferences, or certifications.
• Remote Work: Hybrid model – 2 days in-office, 3 days remote.
• Employee Assistance Programme: 24/7 confidential counselling and legal advice.

Reach out to HR for enrolment forms or questions.`,
  },
  {
    Icon: Users,
    title: "Meet the Team",
    description: "Get to know your colleagues.",
    dark: false,
    content: `Say hi to the people you'll be working with:

• Sarah Nkosi – Head of People. Your go-to for anything HR, culture, or onboarding.
• James van der Merwe – Engineering Lead. Owns the tech roadmap and architecture.
• Priya Naidoo – Product Manager. Bridges the gap between customers and the build.
• Thabo Mokoena – Design Lead. Champions the user experience across every product.
• Lerato Dlamini – Finance Manager. Handles payroll, expenses, and reimbursements.
• Michael Botha – IT & Security. Sets you up with hardware, access, and tooling.

Check the org chart on the intranet for the full team directory.`,
  },
  {
    Icon: LifeBuoy,
    title: "IT & Support",
    description: "Get help with your setup and access.",
    dark: false,
    content: `Need help? Here's how to get sorted quickly:

• Laptop or Hardware Issues: Log a ticket at support.inspired.co.za or email it@inspired.co.za.
• Password Resets: Use the self-service portal at reset.inspired.co.za.
• Software Access Requests: Submit through the IT portal – approvals usually within 4 hours.
• VPN & Remote Access: Setup guide is pinned in the #it-support Slack channel.
• Lost Device: Report immediately to IT and your line manager for a remote wipe.
• Emergency IT Line: +27 11 555 0123 (Mon–Fri, 07:00 – 19:00).

Response times: Critical (1h), High (4h), Normal (1 business day).`,
  },
]

export default function EmployeeDashboard() {
  const { onboardingData, isLoading, loadOnboardingData } =
    useEmployeeOnboarding()
  const navigate = useNavigate()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [welcomeContent, setWelcomeContent] = useState<WelcomeContent | null>(
    null,
  )
  const [videoPlaying, setVideoPlaying] = useState(false)
  const [activeResource, setActiveResource] = useState<
    (typeof QUICK_RESOURCES)[number] | null
  >(null)

  useEffect(() => {
    loadOnboardingData()
  }, [loadOnboardingData])

  useEffect(() => {
    apiClient
      .get<WelcomeContent>(ONBOARDING_WELCOME_CONTENT)
      .then((res) => {
        if (res.data) setWelcomeContent(res.data)
      })
      .catch(() => {})
  }, [])

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        Loading...
      </div>
    )
  }

  const isOffboarding = onboardingData?.onboardingStatus === Onboardingstatus.OFFBOARDING
  const isTerminated = onboardingData?.onboardingStatus === Onboardingstatus.TERMINATED

  const stepCompletion = getStepCompletion(onboardingData)
  const flaggedSteps = getStepFlags(onboardingData)
  const completedCount = stepCompletion.filter(Boolean).length
  const progressPercent = Math.round((completedCount / FORM_STEPS.length) * 100)
  const isOnboardingComplete =
    onboardingData?.onboardingStatus === Onboardingstatus.COMPLETED ||
    onboardingData?.onboardingStatus === Onboardingstatus.APPROVED ||
    onboardingData?.onboardingStatus === Onboardingstatus.SUBMITTED
  const isAwaitingApproval =
    onboardingData?.onboardingStatus === Onboardingstatus.SUBMITTED
  const activeStep = isOnboardingComplete
    ? -1
    : stepCompletion.findIndex((s) => !s)

  const handleStartStep = (stepIndex: number) => {
    navigate("/employee/onboarding", { state: { initialStep: stepIndex } })
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Ambient background glows */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-48 -left-48 w-[600px] h-[600px] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute top-1/2 -right-48 w-[500px] h-[500px] rounded-full bg-primary/8 blur-3xl" />
        <div className="absolute -bottom-32 left-1/3 w-[400px] h-[400px] rounded-full bg-primary/5 blur-3xl" />
      </div>

      <WelcomeDialog />

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* ── Hero ── */}
        <section className="mb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left */}
          <div className="lg:col-span-7 space-y-8">
            <div>
              <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-[11px] font-bold tracking-widest rounded-full mb-6 uppercase ring-1 ring-primary/30 shadow-sm">
                New Beginnings
              </span>
              <h1 className="text-5xl font-extrabold text-foreground tracking-tight leading-[1.1] mb-4">
                Welcome home,{" "}
                <span className="text-primary">
                  {onboardingData?.firstName
                    ? `${onboardingData.firstName}.`
                    : "there."}
                </span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-xl leading-relaxed">
                {onboardingData?.organizationName
                  ? `We're thrilled to have you join ${onboardingData.organizationName}${onboardingData.position ? ` as ${onboardingData.position}` : ""}. Today is the start of your impact.`
                  : "We're thrilled to have you here. Let's get you set up for success."}
              </p>
            </div>

            {/* Progress pill */}
            <div className="flex items-center gap-5 bg-card rounded-2xl shadow-md border border-primary/20 p-5 self-start w-fit ring-1 ring-primary/10">
              <div className="relative w-14 h-14 shrink-0">
                <ProgressRing percent={progressPercent} />
                <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-primary">
                  {progressPercent}%
                </span>
              </div>
              <div>
                <p className="text-sm font-bold text-foreground">
                  {isOffboarding || isTerminated ? "Employment Status" : "Overall Progress"}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {isOffboarding || isTerminated
                    ? (isTerminated ? "Employment ended" : "Offboarding in progress")
                    : `${completedCount} of ${FORM_STEPS.length} steps completed`}
                </p>
                {isAwaitingApproval && !isOffboarding && !isTerminated && (
                  <span className="inline-flex items-center gap-1 mt-1.5 text-xs font-semibold text-amber-600">
                    <CheckCircle className="w-3 h-3" /> Awaiting Approval
                  </span>
                )}
                {!isAwaitingApproval && isOnboardingComplete && !isOffboarding && !isTerminated && (
                  <span className="inline-flex items-center gap-1 mt-1.5 text-xs font-semibold text-green-600">
                    <CheckCircle className="w-3 h-3" /> Approved
                  </span>
                )}
                {(isOffboarding || isTerminated) && (
                  <span className="inline-flex items-center gap-1 mt-1.5 text-xs font-semibold text-orange-600">
                    <Briefcase className="w-3 h-3" /> {isTerminated ? "Terminated" : "Offboarding"}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right: video*/}
          {welcomeContent?.welcomeVideoUrl && (
            <div className="lg:col-span-5 relative group">
              <div className="absolute -inset-4 bg-primary/15 rounded-4xl -z-10 blur-2xl" />
              <div className="relative overflow-hidden rounded-2xl aspect-8/7 bg-slate-900 shadow-2xl">
                <video
                  ref={videoRef}
                  src={welcomeContent.welcomeVideoUrl}
                  className="absolute inset-0 w-full h-full object-cover"
                  onPlay={() => setVideoPlaying(true)}
                  onPause={() => setVideoPlaying(false)}
                  onEnded={() => setVideoPlaying(false)}
                />

                <div className="absolute inset-0 bg-linear-to-t from-slate-900/80 via-transparent to-transparent" />

                {!videoPlaying && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Button
                      className="w-20 h-20 bg-white/20 backdrop-blur-md border border-white/30 rounded-full flex items-center justify-center transition-all hover:scale-110 hover:bg-white/30 group/play"
                      onClick={() => videoRef.current?.play()}
                    >
                      <Play className="w-7 h-7 text-black fill-white translate-x-0.5 transition-transform group-hover/play:translate-x-1" />
                    </Button>
                  </div>
                )}

                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                  <div className="text-black">
                    <p className="text-[10px] text-white font-bold uppercase tracking-widest opacity-60 font-mono">
                      Video Message
                    </p>
                    <p className="font-bold text-lg text-white leading-tight mt-0.5">
                      A Message from{" "}
                      {welcomeContent?.organizationName ??
                        onboardingData?.organizationName ??
                        "Our CEO"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* ── Main content ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left: steps + resources */}
          <div className="lg:col-span-8 space-y-8">
            {isOffboarding || isTerminated ? (
              <div className="bg-card rounded-2xl p-8 border border-border shadow-sm text-center">
                <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="w-7 h-7 text-orange-600" />
                </div>
                <h2 className="text-2xl font-bold text-foreground tracking-tight">
                  {isTerminated ? "Employment Ended" : "Offboarding in Progress"}
                </h2>
                <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                  {isTerminated
                    ? "Your employment with the organisation has ended. Onboarding is no longer available."
                    : "You're currently offboarding, so onboarding steps are no longer available. Check your offboarding page for required documents and next steps."}
                </p>
                {!isTerminated && (
                  <Button
                    onClick={() => navigate("/employee/offboarding")}
                    className="mt-6 bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    View Offboarding
                  </Button>
                )}
              </div>
            ) : (
              <>
            {/* Steps header */}
            <div className="flex items-end justify-between">
              <div>
                <h2 className="text-3xl font-bold text-foreground tracking-tight">
                  {isAwaitingApproval
                    ? "Awaiting Approval"
                    : isOnboardingComplete
                      ? "Completed Steps"
                      : "Your Onboarding"}
                </h2>
                <p className="text-muted-foreground mt-1.5">
                  {isAwaitingApproval
                    ? "Your onboarding has been submitted and is pending review. We will provide feedback as soon as possible."
                    : isOnboardingComplete
                      ? "All steps have been submitted successfully."
                      : "Complete each step to finish your onboarding."}
                </p>
              </div>
              <span className="px-3 py-1 bg-primary/10 text-primary text-xs font-bold rounded-lg shrink-0">
                Step {Math.min(completedCount + 1, FORM_STEPS.length)} of{" "}
                {FORM_STEPS.length}
              </span>
            </div>

            {/* Step cards */}
            <div className="space-y-4">
              {FORM_STEPS.map((step, index) => {
                const isComplete = stepCompletion[index]
                const isFlagged = flaggedSteps[index]
                const isActive = index === activeStep
                const stepNum = String(index + 1).padStart(2, "0")
                const { Icon } = step

                if (isFlagged) {
                  return (
                    <button
                      key={step.id}
                      onClick={() => handleStartStep(index)}
                      className="w-full bg-amber-50 rounded-2xl p-6 flex gap-6 border-l-4 border-amber-400 hover:bg-amber-100 transition-all text-left group"
                    >
                      <div className="shrink-0 text-center w-14">
                        <p className="text-[10px] uppercase font-bold tracking-widest text-amber-600">
                          Fix
                        </p>
                        <p className="text-3xl font-extrabold text-foreground">
                          {stepNum}
                        </p>
                      </div>
                      <div className="grow flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                            <AlertCircle className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <h3 className="font-bold text-foreground">
                              {step.title}
                            </h3>
                            <p className="text-sm text-amber-600 mt-0.5">
                              Corrections required — please update and resubmit
                            </p>
                          </div>
                        </div>
                        <span className="px-3 py-1 bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wider rounded-md shrink-0 ml-4">
                          Needs Correction
                        </span>
                      </div>
                    </button>
                  )
                }

                if (isComplete) {
                  return (
                    <button
                      key={step.id}
                      onClick={() => handleStartStep(index)}
                      className="w-full bg-card rounded-2xl p-6 flex gap-6 border border-border hover:border-green-300/50 hover:bg-muted transition-all text-left group shadow-sm"
                    >
                      <div className="shrink-0 text-center w-14">
                        <p className="text-[10px] uppercase font-bold tracking-widest text-green-600">
                          Done
                        </p>
                        <p className="text-3xl font-extrabold text-foreground">
                          {stepNum}
                        </p>
                      </div>
                      <div className="grow flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <h3 className="font-bold text-foreground">
                              {step.title}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-0.5">
                              {step.description}
                            </p>
                          </div>
                        </div>
                        <span className="px-3 py-1 bg-green-100 text-green-700 text-[10px] font-bold uppercase tracking-wider rounded-md shrink-0 ml-4">
                          Completed
                        </span>
                      </div>
                    </button>
                  )
                }

                if (isActive) {
                  return (
                    <div
                      key={step.id}
                      className="bg-primary/10 rounded-2xl p-6 border-l-4 border-primary transition-all ring-1 ring-primary/20 shadow-md"
                    >
                      <div className="flex gap-6">
                        <div className="shrink-0 text-center w-14">
                          <p className="text-[10px] uppercase font-bold text-primary tracking-widest">
                            {completedCount === 0 ? "Start" : "Next"}
                          </p>
                          <p className="text-3xl font-extrabold text-foreground">
                            {stepNum}
                          </p>
                        </div>
                        <div className="grow">
                          <div className="flex items-start justify-between mb-5">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                                <Icon className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <h3 className="font-bold text-foreground">
                                  {step.title}
                                </h3>
                                <p className="text-sm text-muted-foreground mt-0.5">
                                  {step.description}
                                </p>
                              </div>
                            </div>
                            <span className="px-3 py-1 bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wider rounded-md shrink-0 ml-4">
                              In Progress
                            </span>
                          </div>
                          <Button
                            onClick={() => handleStartStep(index)}
                            className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:-translate-y-0.5 transition-all flex items-center gap-2"
                          >
                            {completedCount > 0 ? "Continue" : "Get Started"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )
                }

                return (
                  <div
                    key={step.id}
                    className="bg-card rounded-2xl p-6 flex gap-6 border border-border transition-all hover:bg-muted group shadow-sm"
                  >
                    <div className="shrink-0 text-center w-14 opacity-40 group-hover:opacity-60 transition-opacity">
                      <p className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">
                        Step
                      </p>
                      <p className="text-3xl font-extrabold text-foreground">
                        {stepNum}
                      </p>
                    </div>
                    <div className="grow flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
                          <Icon className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div>
                          <h3 className="font-bold text-foreground/60 group-hover:text-foreground transition-colors">
                            {step.title}
                          </h3>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            {step.description}
                          </p>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-muted text-muted-foreground text-[10px] font-bold uppercase tracking-wider rounded-md shrink-0 ml-4">
                        Upcoming
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
              </>
            )}

            {/* Quick Resources */}
            <div>
              <div className="flex items-end justify-between mb-4">
                <h2 className="text-2xl font-bold text-foreground tracking-tight">
                  Quick Resources
                </h2>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {QUICK_RESOURCES.map((resource) => {
                  const { Icon, title, description, dark } = resource
                  return dark ? (
                    <button
                      key={title}
                      type="button"
                      onClick={() => setActiveResource(resource)}
                      className="p-5 rounded-2xl bg-foreground text-background flex flex-col justify-between h-32 cursor-pointer hover:bg-foreground/90 transition-colors text-left"
                    >
                      <Icon className="w-5 h-5 text-primary-foreground" />
                      <div>
                        <h4 className="font-bold text-sm">{title}</h4>
                        <p className="text-xs text-background/60 mt-1">
                          {description}
                        </p>
                      </div>
                    </button>
                  ) : (
                    <button
                      key={title}
                      type="button"
                      onClick={() => setActiveResource(resource)}
                      className="p-5 rounded-2xl bg-card flex flex-col justify-between h-32 hover:bg-primary hover:text-primary-foreground transition-all cursor-pointer group text-left"
                    >
                      <Icon className="w-5 h-5 text-primary group-hover:text-primary-foreground transition-colors" />
                      <div>
                        <h4 className="font-bold text-sm text-foreground group-hover:text-primary-foreground transition-colors">
                          {title}
                        </h4>
                        <p className="text-xs text-muted-foreground group-hover:text-primary-foreground/70 mt-1 transition-colors">
                          {description}
                        </p>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* ── Right sidebar ── */}
          <div className="lg:col-span-4 space-y-6">
            {/* Dark progress card */}
            <div className="bg-card text-card-foreground rounded-2xl p-8 border border-border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/20 rounded-full blur-3xl -mr-10 -mt-10" />
              <h4 className="font-bold text-lg mb-6">Onboarding Progress</h4>
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="opacity-60">Overall completion</span>
                  <span className="font-bold">{progressPercent}%</span>
                </div>
                <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-400 rounded-full transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>

              {!isOnboardingComplete && !isOffboarding && !isTerminated && activeStep >= 0 && (
                <div className="pt-6 border-t border-border mt-6">
                  <p className="text-[11px] font-bold uppercase tracking-widest opacity-50 mb-4">
                    Up Next
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
                      {(() => {
                        const { Icon } = FORM_STEPS[activeStep]
                        return <Icon className="w-5 h-5 text-primary" />
                      })()}
                    </div>
                    <div>
                      <p className="text-sm font-bold">
                        {FORM_STEPS[activeStep].title}
                      </p>
                      <p className="text-xs opacity-60 mt-0.5">
                        {FORM_STEPS[activeStep].description}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleStartStep(activeStep)}
                    className="mt-5 w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    {completedCount > 0
                      ? "Continue Where You Left Off"
                      : "Get Started"}
                  </Button>
                </div>
              )}

              {(isOffboarding || isTerminated) && (
                <div className="pt-6 border-t border-border mt-6">
                  <div className="flex items-center gap-2 text-orange-400 mb-2">
                    <Briefcase className="w-4 h-4" />
                    <span className="text-sm font-bold">
                      {isTerminated ? "Employment Ended" : "Offboarding in Progress"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {isTerminated
                      ? "Your employment has ended."
                      : "Onboarding is on hold while your offboarding is in progress."}
                  </p>
                </div>
              )}
              {isAwaitingApproval && !isOffboarding && !isTerminated && (
                <div className="pt-6 border-t border-border mt-6">
                  <div className="flex items-center gap-2 text-amber-400 mb-2">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-bold">Awaiting Approval</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Your submission is under review. We'll notify you once it's
                    approved.
                  </p>
                </div>
              )}
              {!isAwaitingApproval && isOnboardingComplete && (
                <div className="pt-6 border-t border-border mt-6 flex items-center gap-2 text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-bold">
                    All steps completed!
                  </span>
                </div>
              )}
            </div>

            {/* Checklist card */}
            {!isOffboarding && !isTerminated && (
              <div className="bg-card rounded-2xl p-6 border border-border shadow-sm">
                <h4 className="font-bold text-foreground mb-5">Steps Checklist</h4>
                <div className="space-y-4">
                  {FORM_STEPS.map((step, index) => (
                    <div key={step.id} className="flex items-center gap-3">
                      <div
                        className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 border transition-colors ${
                          flaggedSteps[index]
                            ? "bg-amber-100 border-amber-400"
                            : stepCompletion[index]
                              ? "bg-green-100 border-green-400"
                              : index === activeStep
                                ? "bg-primary/10 border-primary ring-2 ring-primary/20"
                                : "bg-muted border-border"
                        }`}
                      >
                        {flaggedSteps[index] && (
                          <AlertCircle className="w-3 h-3 text-amber-600" />
                        )}
                        {!flaggedSteps[index] && stepCompletion[index] && (
                          <CheckCircle className="w-3 h-3 text-green-600" />
                        )}
                      </div>
                      <span
                        className={`text-sm transition-colors ${
                          flaggedSteps[index]
                            ? "font-semibold text-amber-700"
                            : stepCompletion[index]
                              ? "line-through text-muted-foreground"
                              : index === activeStep
                                ? "font-semibold text-foreground"
                                : "text-muted-foreground"
                        }`}
                      >
                        {step.title}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Support card */}
            <div className="bg-card text-card-foreground rounded-2xl p-6 border border-border shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
                  <HelpCircle className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-primary">
                    Support
                  </p>
                  <h4 className="font-bold text-sm text-foreground">
                    Need Help?
                  </h4>
                </div>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                If you're stuck on a step or have questions, our onboarding team
                is here to help.
              </p>
              <a
                href="mailto:temperedtalent.com"
                className="block w-full py-2.5 bg-primary text-primary-foreground hover:bg-primary/90 font-bold rounded-xl text-sm text-center shadow-sm transition-all"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </main>

      <Dialog
        open={activeResource !== null}
        onOpenChange={(open) => !open && setActiveResource(null)}
      >
        <DialogContent className="max-w-lg">
          {activeResource && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <activeResource.Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <DialogTitle>{activeResource.title}</DialogTitle>
                    <DialogDescription>
                      {activeResource.description}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              <div className="whitespace-pre-line text-sm text-foreground/80 leading-relaxed">
                {activeResource.content}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
